/**
 * 🐛 ГЛОБАЛЬНАЯ СИСТЕМА ОТЛАДКИ
 * Централизованное управление всем логированием
 */

// 🎛️ Настройки отладки
interface DebugConfig {
  enabled: boolean           // Главный переключатель всего логирования
  api: boolean              // Детальное логирование API запросов
  copilot: boolean          // Детальное логирование Copilot запросов
  auth: boolean             // Логирование авторизации и токенов
  user: boolean             // Логирование пользователя и контекста
  navigation: boolean       // Логирование навигации и роутинга
  storage: boolean          // Логирование localStorage операций
  performance: boolean      // Логирование производительности
  errors: boolean           // Логирование ошибок (всегда включено)
}

// 🔧 Дефолтные настройки
const defaultConfig: DebugConfig = {
  enabled: process.env.NODE_ENV !== 'production',
  api: true,
  copilot: true,
  auth: true, 
  user: true,
  navigation: false,
  storage: false,
  performance: false,
  errors: true, // Ошибки всегда включены
}

// 📱 Глобальный объект отладки
class GlobalDebug {
  private config: DebugConfig

  constructor() {
    this.config = { ...defaultConfig }
    this.setupGlobalMethods()
  }

  // 🔍 Проверка нужно ли логировать
  shouldLog(category: keyof Omit<DebugConfig, 'enabled'>): boolean {
    return this.config.enabled && this.config[category]
  }

  // 📊 Получить текущую конфигурацию
  getConfig(): DebugConfig {
    return { ...this.config }
  }

  // ⚙️ Установить конфигурацию
  setConfig(newConfig: Partial<DebugConfig>): void {
    this.config = { ...this.config, ...newConfig }
    console.log('🔧 Конфигурация отладки обновлена:', this.config)
  }

  // 🚫 Отключить все логирование
  disableAll(): void {
    this.config.enabled = false
    console.log('🚫 ВСЁ ЛОГИРОВАНИЕ ОТКЛЮЧЕНО')
  }

  // ✅ Включить все логирование  
  enableAll(): void {
    this.config.enabled = true
    Object.keys(this.config).forEach(key => {
      if (key !== 'enabled') {
        this.config[key as keyof DebugConfig] = true
      }
    })
    console.log('✅ ВСЁ ЛОГИРОВАНИЕ ВКЛЮЧЕНО')
  }

  // 🔄 Переключить категорию
  toggle(category: keyof DebugConfig): void {
    if (category === 'enabled') {
      this.config.enabled = !this.config.enabled
      console.log(`🔄 Главный переключатель: ${this.config.enabled ? 'ВКЛ' : 'ВЫКЛ'}`)
    } else {
      this.config[category] = !this.config[category]
      console.log(`🔄 ${category}: ${this.config[category] ? 'ВКЛ' : 'ВЫКЛ'}`)
    }
  }

  // 📋 Показать статус
  status(): void {
    console.group('🐛 СТАТУС ОТЛАДКИ')
    console.log(`🎛️ Главный переключатель: ${this.config.enabled ? '✅ ВКЛ' : '❌ ВЫКЛ'}`)
    console.log(`📡 API запросы: ${this.shouldLog('api') ? '✅ ВКЛ' : '❌ ВЫКЛ'}`)
    console.log(`🤖 Copilot: ${this.shouldLog('copilot') ? '✅ ВКЛ' : '❌ ВЫКЛ'}`)
    console.log(`🔐 Авторизация: ${this.shouldLog('auth') ? '✅ ВКЛ' : '❌ ВЫКЛ'}`)
    console.log(`👤 Пользователь: ${this.shouldLog('user') ? '✅ ВКЛ' : '❌ ВЫКЛ'}`)
    console.log(`🧭 Навигация: ${this.shouldLog('navigation') ? '✅ ВКЛ' : '❌ ВЫКЛ'}`)
    console.log(`💾 Хранилище: ${this.shouldLog('storage') ? '✅ ВКЛ' : '❌ ВЫКЛ'}`)
    console.log(`⚡ Производительность: ${this.shouldLog('performance') ? '✅ ВКЛ' : '❌ ВЫКЛ'}`)
    console.log(`❌ Ошибки: ${this.shouldLog('errors') ? '✅ ВКЛ' : '❌ ВЫКЛ'}`)
    console.groupEnd()
  }

  // 💡 Показать справку
  help(): void {
    console.group('🆘 СПРАВКА ПО ОТЛАДКЕ')
    console.log('🎛️ Основные команды:')
    console.log('  DEBUG.status()           - показать текущий статус')
    console.log('  DEBUG.enableAll()        - включить всё логирование')  
    console.log('  DEBUG.disableAll()       - отключить всё логирование')
    console.log('  DEBUG.help()             - показать эту справку')
    console.log('')
    console.log('🔄 Переключение категорий:')
    console.log('  DEBUG.toggle("enabled")     - главный переключатель')
    console.log('  DEBUG.toggle("api")         - API запросы')
    console.log('  DEBUG.toggle("copilot")     - Copilot запросы')
    console.log('  DEBUG.toggle("auth")        - авторизация')
    console.log('  DEBUG.toggle("user")        - пользователь')
    console.log('  DEBUG.toggle("navigation")  - навигация')
    console.log('  DEBUG.toggle("storage")     - localStorage')
    console.log('  DEBUG.toggle("performance") - производительность')
    console.log('')
    console.log('⚙️ Конфигурация:')
    console.log('  DEBUG.getConfig()        - получить конфигурацию')
    console.log('  DEBUG.setConfig({...})   - установить конфигурацию')
    console.log('')
    console.log('💡 Примеры:')
    console.log('  DEBUG.toggle("api")      - переключить API логи')
    console.log('  DEBUG.setConfig({api: false, auth: true}) - настроить')
    console.groupEnd()
  }

  // 🌐 Настройка глобальных методов
  private setupGlobalMethods(): void {
    if (typeof window !== "undefined") {
      // Добавляем глобальный объект DEBUG
      const debugInstance = this;
      (window as any).DEBUG = debugInstance;

      // Для совместимости со старым API
      (window as any).toggleApiLogging = (enabled?: boolean) => {
        if (enabled !== undefined) {
          debugInstance.setConfig({ api: enabled })
        } else {
          debugInstance.toggle('api')
        }
      }

      (window as any).checkApiLogging = () => {
        console.log(`🐛 API логирование: ${debugInstance.shouldLog('api') ? '✅ ВКЛ' : '❌ ВЫКЛ'}`)
        console.log('💡 Используйте DEBUG.help() для полной справки')
      }
    }
  }
}

// 🌍 Создаем глобальный экземпляр
export const DEBUG = new GlobalDebug()

// 🔧 Вспомогательные функции для логирования
export const debugLog = (category: keyof Omit<DebugConfig, 'enabled'>, ...args: any[]): void => {
  if (DEBUG.shouldLog(category)) {
    console.log(...args)
  }
}

export const debugGroup = (category: keyof Omit<DebugConfig, 'enabled'>, name: string): void => {
  if (DEBUG.shouldLog(category)) {
    console.group(name)
  }
}

export const debugGroupEnd = (category: keyof Omit<DebugConfig, 'enabled'>): void => {
  if (DEBUG.shouldLog(category)) {
    console.groupEnd()
  }
}

export const debugError = (...args: any[]): void => {
  if (DEBUG.shouldLog('errors')) {
    console.error(...args)
  }
}

export const debugWarn = (category: keyof Omit<DebugConfig, 'enabled'>, ...args: any[]): void => {
  if (DEBUG.shouldLog(category)) {
    console.warn(...args)
  }
}

// 📝 Типы для TypeScript
declare global {
  interface Window {
    DEBUG: GlobalDebug
    toggleApiLogging: (enabled?: boolean) => void
    checkApiLogging: () => void
  }
}

// 🎯 Основной экспорт уже выше как именованный экспорт DEBUG 